/**
 * **The Match Debrief screen** — `/um/match/:tmsMatchDbId/debrief` (spec 13 §3, Run 73).
 *
 * The screen a UM opens minutes after the final whistle and works through with the match's
 * Umpire 1, Umpire 2 and Video Umpire. It is the room's agenda and the place the room's answers
 * are written down. **Not the reserve umpire** — the reserve does not attend (spec 13 §1).
 *
 * Authority: `um-app/13-match-debrief.md` §3, §4 and §5; `um-app/12-ai-features.md` §4a (Call 6,
 * which this screen consumes and never re-computes); `um-app/02-capture.md` and
 * `um-app/03-review-release.md` (the capture and release machinery this screen REUSES rather
 * than reimplements); `um-app/08-native-language.md` §3 and §5; `um-app/10-sync-offline.md` §4;
 * `um-app/11-release-and-debrief.md` §2 (the fifteen-minute undo) and §3.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THE TECHNICAL DELEGATE'S RULINGS OF 11 SEPTEMBER 2026, AFTER THE UX REVIEW
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * **U1 — Room View and My View, and Room View is ALWAYS the default.** Not remembered, not
 * per-device, not per-match: every open of this screen opens in Room View, whatever the reader
 * chose last time. The reason is the room. This screen is read at a table with the umpires
 * sitting opposite, and a phone is glanced at across a table — so the default has to be the
 * state that is safe to be seen, and a remembered preference is a state nobody re-checked.
 *
 *   * **Room View hides** the author chips (`UM · Naoko` / `You`), the note-count pills, and the
 *     `worth aligning` content — which renders in My View alone, under its own kicker
 *     `For The Managers`.
 *   * **My View shows everything**, and is one press away.
 *
 * The alignment split itself is `splitSummary` in `matchDebrief.ts`, whose own note sets out why
 * the split is positional, why that is safe, and why its only possible error is to withhold too
 * much rather than too little.
 *
 * **U2 — there is NO umpire-facing disclosure line, and one must not be added.** An earlier
 * draft of this screen carried a sentence addressed to the umpire, telling them what was being
 * written down. It is wrong twice over. Umpires are data subjects and not users (spec 00 §2):
 * they never touch this application and never read it. And debrief responses are usually typed
 * AFTER the umpires have left the room — a manager writing up what was said over a coffee — so a
 * line addressed to somebody who is not there would be furniture at best and, at worst, a claim
 * that this screen is a shared surface. The composer is a manager-only surface and says so by
 * saying nothing.
 *
 * **U3 — a debrief response is JUST A NOTE.** The whole existing lattice applies unchanged, with
 * no special case anywhere: a draft is private to its recorder, `Lock` is available on it,
 * `Release` makes it visible to every UM and the TD at the same moment, and its author has
 * fifteen minutes to undo that. This screen therefore adds no write path of its own — it calls
 * `captureNote`, `lockNote`, `releaseMatchNotes` and `unreleaseNote`, the same four the capture
 * screen calls, through the same offline queue.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHAT THIS SCREEN DELIBERATELY DOES NOT DO
 * ─────────────────────────────────────────────────────────────────────────────
 *
 *  * **It never refreshes the summary under the reader.** A background read notices that the
 *    released-note set has grown and says so in a quiet strip — `2 More Notes Released Since You
 *    Opened This · Refresh Summary` — which is a PRESS. Two managers reading a paragraph aloud
 *    must not have it rewritten mid-sentence. See `moreNotesStrip`.
 *  * **It computes no summary and no alignment.** Both are the API's (`debrief/match-summary.ts`)
 *    and are the same bytes the Technical Delegate's own read will serve in Run 75. A second
 *    implementation on the phone is a second thing to keep true.
 *  * **It carries no TD surface.** Run 75 builds the read-only view with its date and match
 *    filters. The server already serves the summary to a TD session and that is untouched here.
 *  * **It carries no second write path, no toast, and no `window.confirm`.** Every confirmation
 *    and every failure is rendered in the page flow beside the thing it is about — the house
 *    pattern, and the reason `CapturePanel` gives for it.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * OFFLINE, AND THE ONE HONEST LIMIT
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * Capture is fully offline: a response typed with no signal is committed to the phone's queue,
 * renders in `What The Umpire Said` from the queue itself under its `Waiting To Send` mark, and
 * a queued release keeps the capture screen's own string (`Release Queued — N Notes Will Go When
 * You're Back Online`). A failed summary read never disables the composer.
 *
 * The READ side is cached **for the session, in memory** (`lastRead` below), so a re-read that
 * fails renders the last text under `As Of hh:mm` rather than a blank card or a spinner in a
 * meeting. It is not cached across an app restart, and this is stated rather than implied: the
 * durable on-device copy of released notes is spec 10 §2.1's `notes` store, which belongs with
 * the service worker and the pull cursor and which `queueStore.ts` records as deliberately not
 * built — *"a half-populated local mirror that the app read as though it were complete would be
 * exactly the kind of claim spec 10 §1 exists to forbid."* Writing note bodies into
 * `localStorage` to fake it would additionally put released text somewhere the data model keeps
 * it out of. So the cache is honest about its own lifetime, and the composer — the half that
 * cannot be re-fetched — is durable on the phone.
 */

import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";

import {
  languageByCode,
  languageByName,
  undoWindowLabel,
  undoWindowRemainingMs,
} from "@hockeytd/shared";

import {
  captureNote,
  deviceId,
  fetchFixtureDays,
  fetchMatchDebriefSummary,
  fetchMatchUmpires,
  fetchMyNotes,
  fetchPreferredLanguage,
  fetchReleasedNotes,
  lockNote,
  newNoteId,
  releaseMatchNotes,
  unreleaseNote,
} from "../../api/client.js";
import type {
  AppointedUmpireBody,
  FixtureRowView,
  OwnNoteView,
  ReleasedNoteView,
  ReleaseReceiptBody,
  FixtureListBody,
} from "../../api/types.js";
import { HowToLink, HowToUse } from "../../app/HowToUse.js";
import { FailBanner, Skeleton } from "../../app/states.js";
import { useAsync } from "../../app/useAsync.js";
import { useQueueStatus } from "../../app/useQueueStatus.js";
import { UM_ROOT_PATH } from "../../app/UmShell.js";
import {
  LOCKED_RELEASE_WARNING,
  UNDO_RECEIPT_SUFFIX,
  releaseQueuedReceipt,
  undoStillOffered,
} from "../../features/um/CapturePanel.js";
import { NativeNoteReview } from "../../features/um/NativeNoteReview.js";
import {
  DebriefSeatHeader,
  DebriefSeatObservations,
  DebriefSeatSummary,
  type SummaryEntry,
} from "../../features/debrief/DebriefSeat.js";
import {
  debriefSeats,
  moreNotesStrip,
  matchdayLabel,
  releasedResponsesReceipt,
  RESPONSES_HEADING,
  tokyoClockTime,
} from "./matchDebrief.js";

/**
 * The on-screen name of the responses section.
 *
 * **Moved to `matchDebrief.ts` in run 74** — the Consolidated Daily UM Debrief Report prints the
 * same heading on the phone and on paper (spec 13 §8: *"one heading in both places"*), and this
 * page cannot be its home because `UmDebriefPage` would then import from here and close a cycle.
 * Re-exported unchanged so nothing that already named it has to move.
 */
export { RESPONSES_HEADING } from "./matchDebrief.js";

/** The in-flow prompt of finding 9, verbatim, so a test asserts the shipped string. */
export const LEAVE_WITH_UNRELEASED = "Release Before You Leave The Room";

/**
 * THE FOUR HELPERS THIS SCREEN USED TO OWN MOVED IN RUN 75.
 *
 * `DEBRIEF_SEATS` (the U1 · U2 · VU filter, never `RU`), `SEAT_LABEL`, `seatInitials`,
 * `criterionName` and `noteStamp` now live in `matchDebrief.ts`, because the Technical
 * Delegate's read-only Match Debrief screen draws the same three cards from the same bytes and a
 * second copy of any of them would be a second thing to keep true. They are pure functions over
 * their arguments and nothing about them changed in the move; `debriefSeats` is the one addition,
 * and it is the two lines this screen used to inline around `DEBRIEF_SLOTS`.
 *
 * **None of this screen's WRITE machinery moved**, and that is deliberate: see the note at the
 * head of the run-75 section in `matchDebrief.ts`.
 */

/**
 * THE SEAT HEADER, THE SUMMARY AND THE RELEASED-NOTES LIST MOVED IN RUN 75.
 *
 * They are `features/debrief/DebriefSeat.tsx` now, and the author chip went with them — the
 * Technical Delegate's read-only screen draws all three from the same bytes, with the same
 * classes and the same `data-testid`s. `SummaryEntry` is that module's type for the same reason.
 * Nothing about the markup changed in the move, and no write control went with it.
 */

/** What has been read for one match, kept for the session — see the module note on offline. */
interface LastRead {
  released: ReleasedNoteView[];
  summaries: SummaryEntry[];
  /** When it was read, as an instant — rendered as Tokyo `hh:mm` by `tokyoClockTime`. */
  at: string;
}

/**
 * The session's read cache, module-level and per match.
 *
 * Module-level rather than component state because the point of it is to survive the unmount:
 * a manager who walks to Today and back, or whose phone drops the connection between the two,
 * must not be handed a spinner in front of an umpire. It holds only what the API already served
 * to this signed-in session, it never leaves this tab, and it is gone when the tab is.
 */
const lastRead = new Map<number, LastRead>();

/**
 * RUN 80e (QA pass 5) — what the next match's page needs before its own reads land. The route
 * remounts this page per match (run 80d), and a remounted page waited on `/fixtures` before it
 * could draw the matchday and match pickers again, so the picker a reader had just used vanished,
 * took keyboard and VoiceOver focus with it, and never came back offline. The schedule is the same
 * for every match, so the last good one stands in until the fresh read lands, exactly as
 * `lastRead` does for a match's own reads. (Run 80f: the schedule only. The language setting is
 * one manager's preference, changed on the capture screen, and no picker waits on it.)
 */
let lastSchedule: FixtureListBody | null = null;
/** The picker the reader moved the page with, so the next match's page hands focus back to it. */
let pickerToRefocus: "matchday" | "match" | null = null;

/**
 * Empties every module-level store this screen keeps for a session — the read cache, the last good
 * schedule, and a pending focus hand-off. For tests: each case must start as a first open, whatever
 * the case before it read (run 80e).
 */
export function resetMatchDebriefSession(): void {
  lastRead.clear();
  lastSchedule = null;
  pickerToRefocus = null;
}

/** `CapturePanel`'s honesty union, kept identical — see its own note for the whole design. */
type Saving =
  | { status: "idle" }
  | { status: "saving" }
  | { status: "saved"; noteId: string }
  | { status: "queued" }
  | { status: "failed"; error: Error };

/** What a Release press produced. A queued press has no receipt and never borrows one. */
type ReleaseResult =
  | { kind: "sent"; body: ReleaseReceiptBody }
  | { kind: "queued"; count: number };

/**
 * How often the background read checks whether the panel has released anything since this
 * screen was opened. One minute: long enough to cost nothing at a venue, short enough that a
 * note released while two managers walked off the pitch is offered before they sit down.
 */
const POLL_MS = 60_000;

/**
 * RUN 80d (QA pass 4) — ONE MATCH, ONE PAGE. The matchday and match pickers navigate within this
 * route, and a page that stays mounted carries what it holds to the next match: the refresh press
 * and its `Refreshing…`, a write still in flight, and — worst — the first render after a switch,
 * which still holds the last match's reads and wrote them into the session read cache under the
 * new match, so one match's summaries could stand on another's cards. Keying the page on the
 * match remounts it, and the next match starts from nothing but its own reads.
 */
export function UmMatchDebriefRoute(): React.JSX.Element {
  const { tmsMatchDbId } = useParams<{ tmsMatchDbId: string }>();
  return <UmMatchDebriefPage key={tmsMatchDbId ?? ""} />;
}

export function UmMatchDebriefPage(): React.JSX.Element {
  const { tmsMatchDbId } = useParams<{ tmsMatchDbId: string }>();
  const [search] = useSearchParams();
  const navigate = useNavigate();

  const matchId = Number.parseInt(tmsMatchDbId ?? "", 10);
  const competition = search.get("competition");
  const competitionId = competition === "1916" || competition === "1917" ? competition : null;
  const cameFromCapture = search.get("from") === "capture";

  /**
   * RULING U1 — **Room View, on every open, without exception.**
   *
   * A plain `useState` initialiser and nothing else: no `localStorage`, no `sessionStorage`, no
   * search parameter, no prop. The absence is the feature, and it is written here rather than
   * left as an omission somebody later "fixes" by remembering the reader's choice.
   */
  const [view, setView] = useState<"room" | "my">("room");
  const roomView = view === "room";

  /** Bumped by the `Refresh Summary` press and by a write that changed the server's answer. */
  const [readKey, setReadKey] = useState(0);
  /**
   * RUN 80c (QA pass 3) — WHICH match a read was asked for on, so "a read was asked for" can never
   * follow the reader to a match she did not press on. Since run 80d the route also remounts this
   * page per match (`UmMatchDebriefRoute`), which is the guarantee; this stays as the statement of
   * what `Refreshing…` means.
   */
  const [askedFor, setAskedFor] = useState<number | null>(null);
  const [ownKey, setOwnKey] = useState(0);

  /** RUN 80e — the pickers, so a remount can hand focus back to the one the reader used. */
  const matchdayPickerRef = useRef<HTMLSelectElement>(null);
  const matchPickerRef = useRef<HTMLSelectElement>(null);
  useEffect(() => {
    const target =
      pickerToRefocus === "matchday"
        ? matchdayPickerRef.current
        : pickerToRefocus === "match"
          ? matchPickerRef.current
          : null;
    pickerToRefocus = null;
    target?.focus();
  }, []);

  const schedule = useAsync(
    () =>
      fetchFixtureDays().then((body) => {
        lastSchedule = body;
        return body;
      }),
    [],
  );
  const appointed = useAsync(
    () => (Number.isInteger(matchId) ? fetchMatchUmpires(matchId) : Promise.resolve(null)),
    [matchId],
  );
  const released = useAsync(
    () =>
      Number.isInteger(matchId)
        ? fetchReleasedNotes({ match: matchId })
        : Promise.resolve(null),
    [matchId, readKey],
  );
  const mine = useAsync(
    () => (Number.isInteger(matchId) ? fetchMyNotes({ match: matchId }) : Promise.resolve(null)),
    [matchId, ownKey],
  );
  const languages = useAsync(() => fetchPreferredLanguage(), []);
  // RUN 80e — the fresh schedule when there is one, and the last good one until then.
  const scheduleBody = schedule.status === "ready" ? schedule.data : lastSchedule;

  const fixtures: readonly FixtureRowView[] = scheduleBody?.fixtures ?? [];
  const fixture = fixtures.find((row) => row.tmsMatchDbId === matchId) ?? null;

  /**
   * The three seats, in U1 · U2 · VU order and drawn from the APPOINTMENTS — spec 13 §5's own
   * note on the API says so: *"spec 13 leaves who appears on the screen to Run 73, which draws
   * its list from the appointments."*
   *
   * An empty answer is the ordinary answer today, because this system holds no umpire
   * appointments yet, and it is said plainly below rather than filled with the roster: a debrief
   * is held with the three people who did the match, and a list of thirty umpires is not that.
   */
  const appointments: readonly AppointedUmpireBody[] =
    appointed.status === "ready" && appointed.data !== null ? appointed.data.umpires : [];
  const seats: readonly AppointedUmpireBody[] = debriefSeats(appointments);
  const seatKey = seats.map((seat) => seat.tmsPersonId).join(",");

  /**
   * One Call 6 read per umpire, in parallel, and **each one's failure is its own**.
   *
   * A rejected read becomes `{ ok: false }` rather than rejecting the whole set: the debrief
   * still has two other umpires in it, and a screen that went blank because one summary could
   * not be built would have thrown away two summaries and every released note on the screen.
   * The card for the failed one says so and still renders its notes, which were always the
   * evidence (spec 13 §5's `Failure` paragraph).
   */
  const summaries = useAsync(
    () =>
      (seats.length === 0
        ? Promise.resolve<SummaryEntry[]>([])
        : Promise.all(
            seats.map(
              (seat): Promise<SummaryEntry> =>
                fetchMatchDebriefSummary(matchId, seat.tmsPersonId).then(
                  (body): SummaryEntry => ({ tmsPersonId: seat.tmsPersonId, ok: true, body }),
                  (): SummaryEntry => ({ tmsPersonId: seat.tmsPersonId, ok: false }),
                ),
            ),
          )
      ).then((entries) => ({ seatKey, entries })),
    [matchId, seatKey, readKey],
  );

  /* ── The session read cache, and what is actually rendered ───────────────── */

  const liveReleased = released.status === "ready" ? released.data : null;
  /**
   * RUN 80e (QA pass 5) — a summaries answer counts only for the seat list it was read for. Before
   * the appointments land there are no seats and the read resolves to nothing at once; cached as
   * this match's summaries, that nothing overwrote the good ones a revisit would have shown
   * straight away. An answer for any other seat list is not live, and is never cached.
   */
  const liveSummaries =
    summaries.status === "ready" && summaries.data.seatKey === seatKey && seats.length > 0
      ? summaries.data.entries
      : null;

  useEffect(() => {
    if (liveReleased === null || liveSummaries === null) return;
    lastRead.set(matchId, {
      released: liveReleased.notes,
      summaries: liveSummaries,
      at: new Date().toISOString(),
    });
  }, [matchId, liveReleased, liveSummaries]);

  const cached = lastRead.get(matchId) ?? null;
  const shownReleased: readonly ReleasedNoteView[] =
    liveReleased?.notes ?? cached?.released ?? [];
  const shownSummaries: readonly SummaryEntry[] = liveSummaries ?? cached?.summaries ?? [];
  /** Non-null exactly when what is on screen came from the cache rather than from this read. */
  const servedFromCache =
    (released.status === "error" || summaries.status === "error") && cached !== null
      ? tokyoClockTime(cached.at)
      : null;

  /* ── FINDING 4 — the panel released something, and the reader is TOLD, not shown ── */

  /**
   * The background check. It reads the released set again on a timer and stores only a COUNT;
   * nothing it learns is rendered into the summary or the notes until the press below.
   */
  /** The polled count, with the match it was polled on (run 80c; since 80d each match has its own page). */
  const [sinceOpen, setSinceOpen] = useState<{ matchId: number; count: number } | null>(null);
  const shownCount = shownReleased.length;
  useEffect(() => {
    if (!Number.isInteger(matchId)) return undefined;
    const timer = setInterval(() => {
      fetchReleasedNotes({ match: matchId }).then(
        (body) => {
          setSinceOpen({ matchId, count: body.count });
        },
        () => {
          // A failed check changes nothing on screen. It is a background read, and a strip that
          // appeared because a poll timed out would be noise in a meeting.
        },
      );
    }, POLL_MS);
    return () => {
      clearInterval(timer);
    };
  }, [matchId]);
  // RUN 80c — a count polled on another match says nothing about this one.
  const newlyReleased =
    sinceOpen === null || sinceOpen.matchId !== matchId
      ? 0
      : Math.max(0, sinceOpen.count - shownCount);

  /**
   * RUN 80 — the one refresh press, wherever it is drawn. It re-reads the released notes and every
   * seat's summary. While a read the reader asked for is out it says `Refreshing…` and cannot be
   * pressed again, so a second press never queues a second read behind the first. The screen's
   * first read is not one she asked for, so it does not borrow the word.
   */
  const refreshing =
    askedFor === matchId && (released.status === "loading" || summaries.status === "loading");
  const refreshSummaries = (): void => {
    setSinceOpen(null);
    setAskedFor(matchId);
    setReadKey((count) => count + 1);
  };
  /**
   * RUN 80a (QA gate) — while a read she asked for is out, the press says so and nothing else. A
   * poll tick landing mid-read would otherwise compare the fresh count with the notes still on
   * screen and put the strip where `Refreshing…` was.
   */
  const showStrip = newlyReleased > 0 && !refreshing;

  /* ── The reader's own notes, including the ones the queue is still carrying ── */

  const queue = useQueueStatus();
  const fromServer: readonly OwnNoteView[] = mine.status === "ready" && mine.data !== null ? mine.data.notes : [];
  const acknowledged = new Set(fromServer.map((note) => note.id));

  /**
   * Queued captures projected as notes — `CapturePanel`'s own trick, and for its own reason:
   * spec 10 §5 step 6 requires that a force-quit and re-open leaves everything still there, and
   * the queue carries the whole note, so an unsent response needs no second local store to be on
   * screen. The fields the server would have filled are null rather than guessed.
   */
  const queuedOwn: OwnNoteView[] = queue.queuedCaptures
    .filter(
      (write) =>
        write.note.matchId === matchId &&
        write.note.kind === "debrief_response" &&
        !acknowledged.has(write.noteId),
    )
    .map((write) => ({
      id: write.noteId,
      authorUmId: "",
      authorShortName: null,
      umpireId: write.note.umpireId,
      umpireMediaName: null,
      kind: "debrief_response" as const,
      competitionId: write.note.competition,
      matchId: write.note.matchId,
      matchNo: null,
      quarter: write.note.quarter,
      matchMinute: write.note.matchMinute,
      minuteProvisional: write.note.minuteProvisional,
      bodyEn: write.note.bodyEn,
      shortSummary: null,
      tagIds: [...write.note.tagIds],
      criterionStableKey: null,
      aiState: "none" as const,
      capturedAt: write.note.capturedAt,
      visibility: "draft" as const,
      releasedAt: null,
      bodyNative: write.note.bodyNative ?? null,
      nativeLang: write.note.nativeLang ?? null,
    }));

  const own = [...fromServer, ...queuedOwn];
  /** The reader's own responses that have NOT reached the panel — drafts and locked notes. */
  const ownUnreleasedResponses = own.filter(
    (note) => note.kind === "debrief_response" && note.visibility !== "released",
  );
  const ownDraftResponses = ownUnreleasedResponses.filter((note) => note.visibility === "draft");

  /* ── Presses: save, lock, release, undo. All four are the existing paths. ──── */

  const [composerFor, setComposerFor] = useState<string | null>(null);
  const [touched, setTouched] = useState(false);
  const [text, setText] = useState("");
  const [nativeLang, setNativeLang] = useState<string | null>(null);
  const [bodyNative, setBodyNative] = useState("");
  const [saving, setSaving] = useState<Saving>({ status: "idle" });
  const [releasing, setReleasing] = useState(false);
  const [receipt, setReceipt] = useState<ReleaseResult | null>(null);
  const [releaseError, setReleaseError] = useState<Error | null>(null);
  const [locking, setLocking] = useState<string | null>(null);
  const [lockError, setLockError] = useState<Error | null>(null);
  const [confirmUnlock, setConfirmUnlock] = useState<string | null>(null);
  const [undoing, setUndoing] = useState<string | null>(null);
  const [undoError, setUndoError] = useState<Error | null>(null);
  const [reviewing, setReviewing] = useState<string | null>(null);
  const [tick, setTick] = useState(() => Date.now());
  const boxRef = useRef<HTMLTextAreaElement | null>(null);

  /** The countdown ticks only while a released response still has an open window. */
  const undoable = shownReleased.filter(
    (note) =>
      note.kind === "debrief_response" &&
      undoWindowLabel(undoWindowRemainingMs(note.releasedAt, new Date(tick))) !== null,
  ).length;
  useEffect(() => {
    if (undoable === 0) return undefined;
    const timer = setInterval(() => {
      setTick(Date.now());
    }, 1000);
    return () => {
      clearInterval(timer);
    };
  }, [undoable]);

  /**
   * Which umpire the composer is aimed at.
   *
   * It follows the card in view **only while nothing has been typed and no seat has been picked
   * by hand**. A selector that moved under a half-written sentence would file the wrong umpire's
   * words against the right umpire's name, which is the one mistake this screen cannot afford;
   * and once the manager has chosen a seat deliberately, scrolling is not a decision to change
   * it. After a save the selection stays exactly where it was (finding 7).
   */
  const selected = composerFor ?? seats[0]?.tmsPersonId ?? null;
  useEffect(() => {
    if (touched || text !== "" || bodyNative !== "") return undefined;
    if (typeof IntersectionObserver === "undefined" || seats.length === 0) return undefined;
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .map((entry) => entry.target.getAttribute("data-umpire"))
          .find((id): id is string => id !== null);
        if (visible !== undefined) setComposerFor(visible);
      },
      { rootMargin: "-20% 0px -60% 0px" },
    );
    for (const seat of seats) {
      const card = document.querySelector(`[data-umpire="${seat.tmsPersonId}"]`);
      if (card !== null) observer.observe(card);
    }
    return () => {
      observer.disconnect();
    };
  }, [seatKey, touched, text, bodyNative, seats]);

  const composerLanguage =
    nativeLang === null
      ? undefined
      : (languageByCode(nativeLang) ??
        languageByName(languages.status === "ready" ? languages.data.language : "English"));
  const languageChoices = languages.status === "ready" ? languages.data.choices : [];
  const canSave =
    selected !== null &&
    competitionId !== null &&
    (nativeLang === null ? text.trim() !== "" : bodyNative.trim() !== "");

  /**
   * THE SAVE — `captureNote`, the one write seam, with `kind: "debrief_response"`.
   *
   * **No tags and no criterion, ever**: the API refuses both on this kind with a typed 400, and
   * for the reason spec 13 §4 gives — these are the umpire's own words, and filing them against
   * a criterion would record them as an observation ABOUT the umpire, which they are not. There
   * is no tag palette on this composer to be tempted by.
   *
   * The composer stays open, focused and on the same umpire afterwards, because a debrief is a
   * conversation and the next sentence is usually the same person's.
   */
  const save = (): void => {
    if (!canSave || saving.status === "saving" || selected === null || competitionId === null) {
      return;
    }
    setSaving({ status: "saving" });
    const noteId = newNoteId();

    captureNote(noteId, {
      umpireId: selected,
      kind: "debrief_response",
      competition: competitionId,
      matchId,
      // A debrief response is what was said at the debrief, not a moment in the match. There is
      // no stamp to make and none is invented; `minuteProvisional` is false because there is no
      // minute for it to qualify.
      quarter: null,
      matchMinute: null,
      minuteProvisional: false,
      bodyEn: nativeLang === null ? text.trim() : "",
      ...(nativeLang === null ? {} : { bodyNative: bodyNative.trim(), nativeLang }),
      tagIds: [],
      deviceId: deviceId(),
      capturedAt: new Date().toISOString(),
    }).then(
      (outcome) => {
        setSaving(outcome.sent ? { status: "saved", noteId } : { status: "queued" });
        setText("");
        setBodyNative("");
        if (outcome.sent) setOwnKey((count) => count + 1);
        // Focus stays in the box: the fifteen-second target is a manager typing the umpire's
        // next sentence without touching anything but the keyboard.
        boxRef.current?.focus();
      },
      (error: unknown) => {
        setSaving({
          status: "failed",
          error: error instanceof Error ? error : new Error("The response was not saved."),
        });
      },
    );
  };

  /** One release helper for both presses — `CapturePanel`'s rule, because there is one route. */
  const sendRelease = (noteIds: readonly string[], unlock: readonly string[]): void => {
    if (releasing || noteIds.length === 0) return;
    setReleasing(true);
    setReleaseError(null);
    releaseMatchNotes(matchId, noteIds, unlock).then(
      (outcome) => {
        setReleasing(false);
        setReceipt(
          outcome.sent
            ? { kind: "sent", body: outcome.receipt }
            : { kind: "queued", count: noteIds.length },
        );
        setConfirmUnlock(null);
        if (outcome.sent) {
          setOwnKey((count) => count + 1);
          // The released set has genuinely changed because THIS reader changed it, so the
          // summary and the notes are re-read. That is not the finding-4 case: nothing is being
          // swapped under the reader — she pressed the button that changed it.
          setAskedFor(matchId);
          setReadKey((count) => count + 1);
        }
      },
      (error: unknown) => {
        setReleasing(false);
        setReleaseError(error instanceof Error ? error : new Error("The release did not send."));
      },
    );
  };

  const lock = (noteId: string): void => {
    if (locking !== null) return;
    setLocking(noteId);
    setLockError(null);
    lockNote(noteId).then(
      (outcome) => {
        setLocking(null);
        if (outcome.sent) setOwnKey((count) => count + 1);
      },
      (error: unknown) => {
        setLocking(null);
        setLockError(error instanceof Error ? error : new Error("The response was not locked."));
      },
    );
  };

  const undoRelease = (noteId: string): void => {
    if (undoing !== null) return;
    setUndoing(noteId);
    setUndoError(null);
    unreleaseNote(noteId).then(
      () => {
        setUndoing(null);
        setOwnKey((count) => count + 1);
        setAskedFor(matchId);
        setReadKey((count) => count + 1);
      },
      (error: unknown) => {
        setUndoing(null);
        setUndoError(
          error instanceof Error ? error : new Error("The release was not taken back."),
        );
        setOwnKey((count) => count + 1);
        setAskedFor(matchId);
        setReadKey((count) => count + 1);
      },
    );
  };

  /* ── Rendering helpers ───────────────────────────────────────────────────── */

  const matchNo = fixture?.displayMatchNo ?? null;
  const competitionName =
    competitionId === null
      ? null
      : competitionId === "1916"
        ? "Men’s Competition"
        : "Women’s Competition";
  const backTo = cameFromCapture
    ? `${UM_ROOT_PATH}/match/${String(matchId)}?competition=${competitionId ?? ""}`
    : UM_ROOT_PATH;
  const backLabel = cameFromCapture ? `‹ ${matchNo ?? "Capture"}` : "‹ Today";

  const days = [...new Set(fixtures.map((row) => row.date))].sort();
  const day = fixture?.date ?? null;
  const dayFixtures = fixtures.filter((row) => row.date === day);

  const go = (row: FixtureRowView): void => {
    navigate(
      `${UM_ROOT_PATH}/match/${String(row.tmsMatchDbId)}/debrief?competition=${row.competitionId}`,
    );
  };

  /**
   * `Undo Release` on a released response, inside its window and never outside it.
   *
   * Identical in behaviour to the capture screen's, because it IS the same act on the same kind
   * of note (ruling U3). It returns null the instant the window closes — never a disabled
   * control, on `CapturePanel`'s own reasoning.
   */
  const undoButton = (note: ReleasedNoteView, mine: boolean): React.JSX.Element | null => {
    if (!mine) return null;
    const label = undoWindowLabel(undoWindowRemainingMs(note.releasedAt, new Date(tick)));
    if (label === null) return null;
    return (
      <span className="undorelease">
        <button
          type="button"
          className="gbtn gbtn--sm"
          disabled={undoing !== null}
          data-testid={`undo-${note.id}`}
          onClick={() => {
            undoRelease(note.id);
          }}
        >
          {undoing === note.id ? "Undoing…" : "Undo Release"}
        </button>
        <span className="undorelease__left">{label} Left</span>
      </span>
    );
  };

  if (!Number.isInteger(matchId) || competitionId === null) {
    return (
      <>
        <header className="umsect">
          <Link className="umback" to={UM_ROOT_PATH}>
            ‹ Today
          </Link>
          <h2>Match Debrief</h2>
        </header>
        <section className="card">
          <p className="card__body">
            This link does not name a match and a competition, so there is no debrief to open. Go
            back to Today and open the match from there.
          </p>
        </section>
      </>
    );
  }

  return (
    <>
      <header className="umsect">
        <Link className="umback" to={backTo}>
          {backLabel}
        </Link>
        <span className="umsect__kicker" data-testid="md-kicker">
          {[matchNo, competitionName, "Match Debrief"].filter((part) => part !== null).join(" · ")}
        </span>
        <h2>Match Debrief</h2>

        {/*
         * RULING U1's toggle, in the header where it is found without scrolling and pressed
         * without leaving the card the reader is on. `aria-pressed` rather than a checkbox: it
         * is two states of one view and not a setting, and it is never written down anywhere.
         */}
        <div className="mdview" role="group" aria-label="Who this screen is being read by" data-howto="md-view">
          <button
            type="button"
            className={roomView ? "mdview__btn mdview__btn--on" : "mdview__btn"}
            aria-pressed={roomView}
            data-testid="view-room"
            onClick={() => {
              setView("room");
            }}
          >
            Room View
          </button>
          <button
            type="button"
            className={roomView ? "mdview__btn" : "mdview__btn mdview__btn--on"}
            aria-pressed={!roomView}
            data-testid="view-my"
            onClick={() => {
              setView("my");
            }}
          >
            My View
          </button>
        </div>

        {/*
         * The matchday and match selectors — `UmDebriefPage`'s own idiom, for its own reason: a
         * debrief is often held the next morning, and yesterday's match has to be reachable
         * without going back through Today. Native selects rather than a bespoke menu; on a
         * phone the platform's own picker is the accessible one.
         */}
        {days.length > 0 && day !== null ? (
          <label className="dbday" data-howto="md-matchday">
            <span className="dbday__lbl">Matchday</span>
            <select
              className="dbday__sel"
              value={day}
              data-testid="md-matchday-select"
              ref={matchdayPickerRef}
              onChange={(event) => {
                const first = fixtures.find((row) => row.date === event.target.value);
                pickerToRefocus = "matchday";
                if (first !== undefined) go(first);
              }}
            >
              {days.map((entry) => (
                <option key={entry} value={entry}>
                  {matchdayLabel(days, entry)}
                </option>
              ))}
            </select>
          </label>
        ) : null}
        {dayFixtures.length > 0 ? (
          <label className="dbday" data-howto="md-match">
            <span className="dbday__lbl">Match</span>
            <select
              className="dbday__sel"
              value={String(matchId)}
              data-testid="md-match-select"
              ref={matchPickerRef}
              onChange={(event) => {
                const row = fixtures.find(
                  (entry) => String(entry.tmsMatchDbId) === event.target.value,
                );
                pickerToRefocus = "match";
                if (row !== undefined) go(row);
              }}
            >
              {dayFixtures.map((row) => (
                <option key={row.tmsMatchDbId} value={String(row.tmsMatchDbId)}>
                  {`${row.displayMatchNo} · ${row.time}`}
                </option>
              ))}
            </select>
          </label>
        ) : null}

        <HowToUse id="um-match-debrief" title="How to use the Match Debrief">
          <li>
            This screen is the post-match debrief with <b>Umpire 1</b>, <b>Umpire 2</b> and the{" "}
            <b>Video Umpire</b>. The reserve umpire is not part of it.
          </li>
          {/* FINDING 14's room-safety line, first among the things that matter. */}
          <li>
            <HowToLink to="md-view">Room View</HowToLink> is how this screen always opens, because
            a phone on a table is read from both sides of it. It leaves out who wrote which note,
            the note counts, and the line about two managers reading something differently.{" "}
            <b>My View</b> shows all of it and is one press away.
          </li>
          <li>
            Each umpire&rsquo;s block is the panel&rsquo;s own released words, organised. Tap{" "}
            <b>Show Notes</b> to read the released notes it was built from — they are the
            evidence, whatever the summary says.
          </li>
          <li>
            <b>Refresh Summary</b>, above the umpire cards, reads the released notes and the
            summaries again. The screen never changes by itself: when a colleague releases a note
            while you are reading, a strip saying so takes the press&rsquo;s place.
          </li>
          <li>
            <HowToLink to="md-composer">What did the umpire ask or say?</HowToLink> writes down
            what they said back. It saves as a <b>draft</b>: private to you, on no other
            manager&rsquo;s screen and not on the Technical Delegate&rsquo;s, until you release
            it.
          </li>
          <li>
            <b>Release Responses</b> is what sends them. From that moment they are visible to the
            whole panel and the Technical Delegate, and you have fifteen minutes to take a release
            back with <b>Undo Release</b>.
          </li>
          <li>
            <HowToLink to="md-matchday">The matchday and match pickers</HowToLink> reach any match
            of the tournament, which is how you hold the next morning&rsquo;s debrief for
            yesterday&rsquo;s match.
          </li>
        </HowToUse>
      </header>

      {/*
       * FINDING 4. In the flow, quiet, and a press — never an automatic swap.
       *
       * RUN 80 — and the press is ALWAYS there. The Technical Delegate, live-testing on M01
       * (13 September 2026), could not find it at all: it was drawn only after a colleague released
       * something while the screen was open, so on an ordinary read there was nothing to find. A
       * quiet `Refresh Summary` now sits above the umpire cards whenever there are cards, and the
       * strip takes its place when the released set has grown — the same press, saying why. One
       * page-level press rather than one per card, because it re-reads every seat at once.
       */}
      {/*
       * RUN 80c (QA pass 3) — drawn above umpire cards, and only there. On a match with no seats
       * nothing on this screen could change after a refresh — its released notes are on the
       * umpire's own evidence screen, as the card below says — so neither the strip nor the press
       * is offered, and a press can no longer take the reader's focus away with the count it
       * cleared. (Run 80b kept a pressed control alive instead, and it followed her to other
       * matches while this page still outlived a match; run 80d remounts it per match.)
       */}
      {seats.length > 0 ? (
        <div className={showStrip ? "mdrefreshbar mdrefreshbar--strip" : "mdrefreshbar"}>
          {/*
           * RUN 80a (QA gate) — ONE button in every state, so a press never loses the reader's
           * place: the strip and the quiet press are the same element wearing a different face,
           * and `aria-disabled` rather than `disabled` keeps keyboard and VoiceOver focus on it
           * while the read is out. The status line tells a screen reader it is refreshing, which a
           * label change alone does not.
           */}
          <button
            type="button"
            className={showStrip ? "mdrefresh" : "gbtn gbtn--sm gbtn--quiet"}
            data-testid={showStrip ? "md-refresh" : "md-refresh-now"}
            aria-disabled={refreshing}
            onClick={() => {
              if (refreshing) return;
              refreshSummaries();
            }}
          >
            {refreshing
              ? "Refreshing…"
              : showStrip
                ? moreNotesStrip(newlyReleased)
                : "Refresh Summary"}
          </button>
          <span className="sr-only" role="status" data-testid="md-refresh-status">
            {refreshing ? "Refreshing The Summaries" : ""}
          </span>
        </div>
      ) : null}

      {/* Never a blank card in a meeting: the last read stands, stamped with when it was made. */}
      {servedFromCache !== null ? (
        <p className="dbainote" data-testid="md-stale">
          {`The Panel Could Not Be Reached Just Now · Showing What Was Last Read · As Of ${servedFromCache}`}
        </p>
      ) : null}

      {(released.status === "loading" || summaries.status === "loading") && cached === null ? (
        <Skeleton rows={5} />
      ) : null}
      {appointed.status === "error" ? <FailBanner error={appointed.error} /> : null}

      {appointed.status === "ready" && seats.length === 0 ? (
        <section className="card">
          <div className="card__head">
            <span className="card__kicker">Nobody Is Appointed To This Match</span>
          </div>
          <p className="card__body">
            The umpires managers&rsquo; appointments for this match are not held in this system,
            so there is no panel to draw. Nothing has been lost: every released note about this
            match is on the umpire&rsquo;s own evidence screen.
          </p>
        </section>
      ) : null}

      {seats.map((seat) => {
        const entry = shownSummaries.find((row) => row.tmsPersonId === seat.tmsPersonId) ?? null;
        const body = entry !== null && entry.ok ? entry.body : null;
        const observations = shownReleased.filter(
          (note) => note.umpireId === seat.tmsPersonId && note.kind === "observation",
        );
        const releasedResponses = shownReleased.filter(
          (note) => note.umpireId === seat.tmsPersonId && note.kind === "debrief_response",
        );
        const ownResponses = ownUnreleasedResponses.filter(
          (note) => note.umpireId === seat.tmsPersonId,
        );
        const ownUnreleasedObservations = own.filter(
          (note) =>
            note.umpireId === seat.tmsPersonId &&
            note.kind === "observation" &&
            note.visibility !== "released",
        ).length;

        return (
          <section
            className="card mdcard"
            key={seat.tmsPersonId}
            data-umpire={seat.tmsPersonId}
            data-testid={`md-card-${seat.tmsPersonId}`}
          >
            <DebriefSeatHeader seat={seat} />

            {/* THE ORGANISED SUMMARY — `DebriefSeat.tsx`, shared with the TD's read-only
                screen. `roomView` is ruling U1's one switch and the only thing this screen
                passes that the Control Centre's does not hold permanently true. */}
            <DebriefSeatSummary seat={seat} entry={entry} roomView={roomView} />

            {/* My View's own line about the reader's OWN unreleased notes — her count, for her
                own eyes, with the way to release them. It is C3-safe by construction: it is
                computed from `GET /notes/mine` and names nobody else. */}
            {!roomView && body !== null && body.state === "no_notes" && ownUnreleasedObservations > 0 ? (
              <p className="mdai__notice" data-testid={`md-own-unreleased-${seat.tmsPersonId}`}>
                {`You Have ${String(ownUnreleasedObservations)} Unreleased ${
                  ownUnreleasedObservations === 1 ? "Note" : "Notes"
                } On This Match. `}
                <Link to={`${UM_ROOT_PATH}/match/${String(matchId)}?competition=${competitionId}`}>
                  Release Them
                </Link>
              </p>
            ) : null}

            {/*
             * THE RELEASED NOTES, behind a native `<details>` — closed by default.
             *
             * Native rather than a hand-rolled disclosure, and this is the only one on the screen:
             * `<details>`/`<summary>` is keyboard-operable and announced by a screen reader with
             * no code at all, and the browser's own find-in-page opens it. What it is given is the
             * house kicker classes, so it looks like every other section header here.
             */}
            <details className="mddisc">
              <summary className="mddisc__sum">
                <span className="card__kicker">Show Notes ▾</span>
                {roomView ? null : (
                  <span className="card__note num" data-testid={`md-notecount-${seat.tmsPersonId}`}>
                    {observations.length === 1 ? "1 Note" : `${String(observations.length)} Notes`}
                  </span>
                )}
              </summary>
              <div className="mddisc__body">
                <DebriefSeatObservations observations={observations} roomView={roomView} />
                {/*
                 * THE HONEST LINE. It is the reader's OWN count of what she has not released —
                 * never anybody else's, and it never names who. Room View does not carry it: what
                 * is withheld from the panel is not a fact for the table.
                 */}
                {!roomView && ownUnreleasedObservations > 0 ? (
                  <p className="mdnotes__held" data-testid={`md-held-${seat.tmsPersonId}`}>
                    {`${String(ownUnreleasedObservations)} ${
                      ownUnreleasedObservations === 1 ? "Note" : "Notes"
                    } Not Yet Released`}
                  </p>
                ) : null}
              </div>
            </details>

            {/* WHAT THE UMPIRE SAID — open by default, because it is the point of the screen. */}
            <div className="mdsaid">
              <div className="notelist__head">
                <span className="card__kicker">{RESPONSES_HEADING}</span>
                {roomView ? null : (
                  <span className="card__note num">
                    {releasedResponses.length + ownResponses.length}
                  </span>
                )}
              </div>
              {releasedResponses.length === 0 && ownResponses.length === 0 ? (
                <p className="card__body">Nothing has been written down yet.</p>
              ) : (
                <ul className="mdnotes">
                  {releasedResponses.map((note) => (
                    <li className="mdnote" key={note.id}>
                      {roomView ? null : (
                        <span className="mdnote__au num">
                          {`Recorded By UM · ${note.authorShortName ?? "Unattributed"}`}
                        </span>
                      )}
                      <span className="mdnote__txt">{note.bodyEn}</span>
                      {undoButton(
                        note,
                        own.some((row) => row.id === note.id),
                      )}
                    </li>
                  ))}
                  {ownResponses.map((note) => (
                    <li className="mdnote mdnote--draft" key={note.id}>
                      <span className="mdnote__draft">
                        {note.visibility === "locked"
                          ? "Locked · Not Yet Shared"
                          : "Draft · Not Yet Shared"}
                      </span>
                      {roomView ? null : <span className="mdnote__au num">Recorded By You</span>}
                      <span className="mdnote__txt">
                        {note.bodyEn === "" ? (note.bodyNative ?? "") : note.bodyEn}
                      </span>
                      {queue.pendingNoteIds.includes(note.id) ? (
                        <span className="pendmark">◌ Waiting To Send</span>
                      ) : null}
                      {queue.queuedReleaseNoteIds.includes(note.id) ? (
                        <span className="pendmark pendmark--release">◍ Release Queued</span>
                      ) : null}
                      <div className="noterow__acts">
                        {/*
                         * SPEC 08 §3's review surface, reached from a native response exactly as
                         * it is reached from a native observation. **Release is blocked until the
                         * English has been reviewed** — the API refuses a release naming an
                         * unreviewed native note, whatever this screen does — so this button is
                         * the way TO the gate rather than the gate itself.
                         */}
                        {note.bodyNative !== null && reviewing !== note.id ? (
                          <button
                            type="button"
                            className="gbtn gbtn--sm"
                            data-testid={`review-native-${note.id}`}
                            onClick={() => {
                              setReviewing(note.id);
                            }}
                          >
                            Review Translation
                          </button>
                        ) : null}
                        {note.visibility === "draft" ? (
                          <button
                            type="button"
                            className="gbtn gbtn--sm"
                            disabled={locking !== null}
                            aria-label="Lock this response"
                            onClick={() => {
                              lock(note.id);
                            }}
                          >
                            {locking === note.id ? "Locking…" : "🔒 Lock"}
                          </button>
                        ) : (
                          <button
                            type="button"
                            className="gbtn gbtn--sm"
                            disabled={releasing}
                            aria-label="Release this locked response"
                            onClick={() => {
                              setConfirmUnlock(note.id);
                              setReleaseError(null);
                            }}
                          >
                            Release
                          </button>
                        )}
                      </div>
                      {reviewing === note.id ? (
                        <NativeNoteReview
                          note={note}
                          releasing={releasing}
                          onChanged={() => {
                            setOwnKey((count) => count + 1);
                          }}
                          onRelease={(noteId) => {
                            sendRelease(
                              [noteId],
                              note.visibility === "locked" ? [noteId] : [],
                            );
                            setReviewing(null);
                          }}
                          onClose={() => {
                            setReviewing(null);
                          }}
                        />
                      ) : null}
                      {confirmUnlock === note.id ? (
                        <div
                          className="lockconfirm"
                          role="dialog"
                          aria-modal="false"
                          aria-labelledby={`mdLock-${note.id}`}
                          data-testid="unlock-confirm"
                        >
                          {/* ALFRED'S WORDS, VERBATIM — the same string the capture screen
                              shows, imported rather than retyped so the two cannot drift. */}
                          <b id={`mdLock-${note.id}`}>{LOCKED_RELEASE_WARNING}</b>
                          <div className="lockconfirm__acts">
                            <button
                              type="button"
                              className="gbtn gbtn--solid"
                              data-testid="unlock-confirm-yes"
                              disabled={releasing}
                              onClick={() => {
                                sendRelease([note.id], [note.id]);
                              }}
                            >
                              {releasing ? "Releasing…" : "Yes, Unlock And Release"}
                            </button>
                            <button
                              type="button"
                              className="gbtn"
                              data-testid="unlock-confirm-no"
                              onClick={() => {
                                setConfirmUnlock(null);
                              }}
                            >
                              No, Keep It Locked
                            </button>
                          </div>
                        </div>
                      ) : null}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </section>
        );
      })}

      {undoError !== null ? <FailBanner error={undoError} /> : null}
      {lockError !== null ? <FailBanner error={lockError} /> : null}
      {releaseError !== null ? <FailBanner error={releaseError} /> : null}

      {receipt !== null ? (
        <p
          className={receipt.kind === "sent" ? "receipt" : "receipt receipt--queued"}
          role="status"
          data-testid="md-receipt"
        >
          {receipt.kind === "sent"
            ? `${releasedResponsesReceipt(receipt.body.released.length)}${
                undoStillOffered(receipt.body.undoUntil, tick) ? UNDO_RECEIPT_SUFFIX : ""
              }`
            : releaseQueuedReceipt(receipt.count)}
        </p>
      ) : null}

      {/*
       * FINDING 9's leaving prompt — IN THE FLOW, above the composer, never a browser dialogue.
       *
       * A `beforeunload` prompt would fire on a reload and not on the bottom bar, would be
       * unstyled, and would be the app shouting at a manager in a room. This is a sentence in the
       * page with the press beside it, and it is on screen the whole time the responses are
       * unreleased rather than only at the moment somebody tries to leave.
       */}
      {ownUnreleasedResponses.length > 0 ? (
        <div className="mdleave" data-testid="md-leave-prompt">
          <b>{LEAVE_WITH_UNRELEASED}</b>
          <span className="mdleave__sub">
            {`${String(ownUnreleasedResponses.length)} ${
              ownUnreleasedResponses.length === 1 ? "response is" : "responses are"
            } still yours alone. Nobody on the panel can read them and neither can the Technical Delegate.`}
          </span>
          <button
            type="button"
            className="gbtn gbtn--solid"
            data-testid="md-release"
            disabled={releasing || ownDraftResponses.length === 0}
            onClick={() => {
              sendRelease(
                ownDraftResponses.map((note) => note.id),
                [],
              );
            }}
          >
            {releasing ? "Releasing…" : "Release Responses"}
          </button>
          {ownDraftResponses.length === 0 ? (
            <p className="btnhint">
              The responses left here are locked. Release one from its own row, which asks first.
            </p>
          ) : null}
        </div>
      ) : null}

      {/*
       * THE COMPOSER — one, sticky, at the foot, and the only place a response is written.
       *
       * RULING U2: there is no line here addressed to an umpire. See the module note.
       */}
      {seats.length === 0 ? null : (
        <div className="mdcomposer" data-howto="md-composer">
          <div className="mdcomposer__seats" role="group" aria-label="Which umpire said it">
            {seats.map((seat) => (
              <button
                key={seat.tmsPersonId}
                type="button"
                className={
                  selected === seat.tmsPersonId
                    ? "mdcomposer__seat mdcomposer__seat--on"
                    : "mdcomposer__seat"
                }
                aria-pressed={selected === seat.tmsPersonId}
                data-testid={`seat-${seat.slot}`}
                onClick={() => {
                  setTouched(true);
                  setComposerFor(seat.tmsPersonId);
                }}
              >
                {seat.slot}
              </button>
            ))}
          </div>

          <label className="field">
            <span className="field__label">
              {composerLanguage === undefined
                ? "What did the umpire ask or say?"
                : `Write In ${composerLanguage.name}`}
            </span>
            <textarea
              ref={boxRef}
              rows={2}
              value={composerLanguage === undefined ? text : bodyNative}
              style={composerLanguage === undefined ? undefined : { fontFamily: composerLanguage.fontStack }}
              placeholder={composerLanguage === undefined ? "What did the umpire ask or say?" : undefined}
              aria-label="What did the umpire ask or say?"
              onChange={(event) => {
                setSaving((current) => (current.status === "saved" ? { status: "idle" } : current));
                if (composerLanguage === undefined) setText(event.target.value);
                else setBodyNative(event.target.value);
              }}
            />
          </label>

          <div className="mdcomposer__acts">
            <button
              type="button"
              className="gbtn gbtn--solid"
              data-testid="md-save"
              disabled={!canSave || saving.status === "saving"}
              onClick={save}
            >
              {saving.status === "saving" ? "Saving…" : "Save Response"}
            </button>
            {/* Spec 08 §3's per-note switch, offered only when the language list has landed. */}
            {languageChoices.length > 0 ? (
              <button
                type="button"
                className="gbtn gbtn--sm"
                data-testid="md-another-language"
                onClick={() => {
                  if (nativeLang !== null) {
                    setNativeLang(null);
                    return;
                  }
                  const preferred = languageByName(
                    languages.status === "ready" ? languages.data.language : "English",
                  );
                  setNativeLang(
                    preferred !== undefined && preferred.name !== "English"
                      ? preferred.code
                      : (languageChoices.find((choice) => choice.name !== "English")?.code ?? null),
                  );
                }}
              >
                {nativeLang === null ? "Write In Another Language" : "Write In English Instead"}
              </button>
            ) : null}
          </div>

          {/*
           * SAVE HONESTY — `CapturePanel`'s three marks, word for word, because they are the
           * same three facts. `Saved ✓` is earned by a reply and by nothing else; a note that is
           * only on the phone says exactly that. Never a toast.
           */}
          {saving.status === "saved" ? (
            <p className="savemark savemark--confirmed" role="status" data-testid="md-saved">
              Saved ✓ · Draft · Not Yet Shared
            </p>
          ) : null}
          {saving.status === "queued" ? (
            <p className="savemark savemark--queued" role="status" data-testid="md-queued">
              Saved On This Phone · Waiting To Send
            </p>
          ) : null}
          {saving.status === "failed" ? <FailBanner error={saving.error} /> : null}
        </div>
      )}
    </>
  );
}
